import { Alert, AlertActionButton, Box } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { transcriptsPath } from "@outschool/routes";
import {
  CLASSROOM_BOX_SX,
  CanceledAnnouncement,
  ClassHeaderDetails,
} from "@outschool/ui-components-classroom";
import {
  Subscript,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import { useNodeRef } from "@outschool/ui-utils";
import React, { useState } from "react";

import SectionStatusLabel from "../../../shared/client/components/SectionStatusLabel";
import * as Enrollment from "../../../shared/Enrollment";
import * as Routes from "../../../shared/Routes";
import * as Section from "../../../shared/Section";
import ShareEnrollmentModal from "../enrolling/ShareEnrollmentModal";
import Link from "../Link";

export default function EnrolledClassHeader({
  nonGiftEnrollments,
  activity,
  section,
  userTimeZone,
}) {
  const { t } = useTranslation(
    "client\\components\\enrolled\\EnrolledClassHeader",
  );
  const hasClassroomAccess = nonGiftEnrollments.some(
    e =>
      !Enrollment.isWithdrawn(e) &&
      (!Enrollment.isOngoing(e) || !(e.isEnded || e.isPaused)),
  );

  return (
    <Box
      sx={{
        backgroundColor: "#f1f2f3",
        borderBottom: "1px solid #ddd",
        paddingY: "0.75em",
      }}
    >
      {section?.canceled_at && (
        <Box sx={CLASSROOM_BOX_SX}>
          <CanceledAnnouncement section={section} />
        </Box>
      )}
      <Box sx={CLASSROOM_BOX_SX}>
        <Subscript sx={{ marginBottom: "small" }}>
          {Section.isPast(section) ? (
            <Link to={transcriptsPath()}>{t`Your completed classes`}</Link>
          ) : (
            <Link
              to={Routes.learnSchedulePath()}
            >{t`Your enrolled classes`}</Link>
          )}
          {hasClassroomAccess && (
            <SectionStatusLabel
              section={section}
              sx={{ marginLeft: "small" }}
            />
          )}
        </Subscript>
        <ClassHeaderDetails
          activity={activity}
          section={section}
          userTimeZone={userTimeZone}
        />
      </Box>
    </Box>
  );
}

export function useInviteLearnersPrompt({ activity, section }) {
  const { t } = useTranslation(
    "client\\components\\enrolled\\EnrolledClassHeader",
  );

  if (!activity || !section) {
    return null;
  }

  const availableSpaces = Section.availableSpaces(section);
  const enrollmentsNeeded = Section.enrollmentsNeededForMinimum(section);

  if (Section.isPast(section)) {
    return null;
  }

  if (enrollmentsNeeded) {
    return t(
      "{{numLearners}} more learner needed to reach the ideal class size.",
      {
        numLearners: enrollmentsNeeded,
        count: enrollmentsNeeded,
        defaultValue_plural:
          "{{numLearners}} more learners needed to reach the ideal class size.",
      },
    );
  } else if (availableSpaces) {
    return activity.isClub
      ? t`Group has space for more learners.`
      : t("Class has space for {{availableSpaces}} more learner.", {
          availableSpaces,
          count: availableSpaces,
          defaultValue_plural:
            "Class has space for {{availableSpaces}} more learners.",
        });
  } else {
    return null;
  }
}

export function InviteLearnersStatusBar({ activity, section, invitePrompt }) {
  const { t } = useTranslation(
    "client\\components\\enrolled\\EnrolledClassHeader",
  );
  const [showShareModal, setShowShareModal] = useState(false);
  const [inviteLearnersNode, inviteLearnersRef] = useNodeRef();

  const { trackTouch: trackInviteLearnersLinkClick } = useImpressionTracking({
    node: inviteLearnersNode,
    trackingLabel: "invite_learners_classroom_banner_link",
    sharedProperties: {
      activityUid: activity && activity.uid,
      sectionUid: section && section.uid,
    },
    uniqueId: section && section.uid,
  });

  return (
    <>
      <Alert
        severity="info"
        action={
          <AlertActionButton
            ref={inviteLearnersRef}
            onClick={() => {
              setShowShareModal(true);
              trackInviteLearnersLinkClick();
            }}
          >
            {t`Invite Learners`}
          </AlertActionButton>
        }
      >
        {invitePrompt}
      </Alert>
      <ShareEnrollmentModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        activity={activity}
        section={section}
      />
    </>
  );
}
